import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { Globe, TrendingUp, Users, BarChart3, Zap, ChevronDown, ChevronUp, Database, Brain, LineChart, Search, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    CartesianGrid
} from 'recharts';
import EnginePredictionBanner from '../components/dashboard/EnginePredictionBanner';

export default function GlobalPredictions() {
    const [fixtures, setFixtures] = useState([]);
    const [selectedMatch, setSelectedMatch] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedSource, setExpandedSource] = useState(null);
    const [intelStatus, setIntelStatus] = useState(null);
    const [intelData, setIntelData] = useState(null);
    const [scraping, setScraping] = useState(false);

    useEffect(() => {
        API.get('/fixtures').then(res => {
            const fx = res.data.fixtures || [];
            setFixtures(fx);
            const upcoming = fx.filter(f => f.status !== 'completed');
            if (upcoming.length) setSelectedMatch(upcoming[0].id);
        });
        API.get('/intelligence/status').then(res => setIntelStatus(res.data)).catch(() => {});
        API.get('/intelligence/latest').then(res => {
            if (res.data.status === 'ready') setIntelData(res.data.report);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (!selectedMatch) return;
        setLoading(true);
        API.get(`/global-predictions/${selectedMatch}`).then(res => {
            setData(res.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [selectedMatch]);

    if (!data && !loading) {
        return (
            <div className="space-y-6" data-testid="global-predictions-page">
                <div className="glass-card p-6">
                    <div className="flex items-center gap-3">
                        <Globe className="w-5 h-5 text-amber-400" />
                        <h2 className="heading-primary text-base md:text-xl text-white">GLOBAL PREDICTION INTELLIGENCE</h2>
                        <div className="badge-ai">DATA-DRIVEN</div>
                    </div>
                </div>
                <div className="glass-card p-6">
                    <Select value={selectedMatch} onValueChange={setSelectedMatch}>
                        <SelectTrigger data-testid="gp-match-selector" className="bg-black/50 border-white/10 text-white h-12 max-w-lg">
                            <SelectValue placeholder="Select match for analysis..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-white/10">
                            {fixtures.filter(f => f.status !== 'completed').map(f => (
                                <SelectItem key={f.id} value={f.id} className="text-white hover:bg-white/10">
                                    {f.home_team?.short_name} vs {f.away_team?.short_name} - {f.date?.slice(0, 10)} - {f.venue?.city}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
        );
    }

    if (loading || !data) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
                <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const d = data;
    const typeIcons = { model: <Database className="w-4 h-4 text-blue-400" />, expert: <Brain className="w-4 h-4 text-purple-400" />, market: <LineChart className="w-4 h-4 text-green-400" /> };

    const handleScrape = async () => {
        setScraping(true);
        try {
            const res = await API.post('/intelligence/scrape', {
                team_a: d?.team_a?.short_name || '',
                team_b: d?.team_b?.short_name || '',
            });
            setIntelData(res.data);
        } catch (err) { console.error('Scrape failed', err); }
        setScraping(false);
    };

    const radarData = d.expert_sources?.map(s => ({
        source: s.source.replace(' Model', '').replace(' Index', '').replace(' Rating', ''),
        team_a: s.team_a_pct,
        team_b: s.team_b_pct,
    })) || [];

    return (
        <div className="space-y-6" data-testid="global-predictions-page">
            <EnginePredictionBanner engineKey="god_mode" matchId={selectedMatch} />
            <div className="glass-card p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <Globe className="w-5 h-5 text-amber-400" />
                        <h2 className="heading-primary text-base md:text-xl text-white">GLOBAL PREDICTION INTELLIGENCE</h2>
                        <div className="badge-ai">DATA-DRIVEN</div>
                    </div>
                    <Select value={selectedMatch} onValueChange={setSelectedMatch}>
                        <SelectTrigger data-testid="gp-match-selector" className="bg-black/50 border-white/10 text-white h-10 max-w-xs">
                            <SelectValue placeholder="Select match..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-white/10">
                            {fixtures.filter(f => f.status !== 'completed').map(f => (
                                <SelectItem key={f.id} value={f.id} className="text-white hover:bg-white/10">
                                    {f.home_team?.short_name} vs {f.away_team?.short_name} - {f.date?.slice(0, 10)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Final Consensus */}
            <div className="glass-card p-6 border-l-4 border-l-amber-500" data-testid="final-consensus">
                <div className="flex items-center gap-2 mb-4">
                    <Zap className="w-4 h-4 text-amber-400" />
                    <h3 className="heading-primary text-sm text-white">FINAL CONSENSUS</h3>
                    <span className="text-[10px] text-white/30 tech-mono ml-2">{d.final_consensus?.sources_count} SOURCES</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                        <div className="tech-mono text-[10px] text-white/30 mb-1">{d.team_a?.short_name}</div>
                        <div className="heading-primary text-4xl neon-text">{d.final_consensus?.team_a_pct}%</div>
                    </div>
                    <div className="space-y-3 pt-2">
                        <div className="prob-bar h-3">
                            <div className="prob-bar-fill bg-cyan-500" style={{ width: `${d.final_consensus?.team_a_pct}%` }} />
                        </div>
                        <div className="text-center tech-mono text-[10px] text-white/30">
                            CONFIDENCE: {((d.final_consensus?.confidence || 0.5) * 100).toFixed(0)}%
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="tech-mono text-[10px] text-white/30 mb-1">{d.team_b?.short_name}</div>
                        <div className="heading-primary text-4xl neon-text-pink">{d.final_consensus?.team_b_pct}%</div>
                    </div>
                </div>
            </div>

            {/* Consensus Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {d.consensus_breakdown?.map((cb, i) => {
                    const colors = ['border-l-cyan-500', 'border-l-blue-500', 'border-l-purple-500', 'border-l-green-500'];
                    const textColors = ['text-cyan-400', 'text-blue-400', 'text-purple-400', 'text-green-400'];
                    return (
                        <div key={i} className={`glass-card p-4 border-l-4 ${colors[i]}`} data-testid={`consensus-source-${i}`}>
                            <div className="tech-mono text-[10px] text-white/30">{cb.source}</div>
                            <div className={`heading-primary text-2xl ${textColors[i]}`}>{cb.team_a_pct}%</div>
                            <div className="text-[10px] text-white/20">Weight: {cb.weight}</div>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Radar Chart */}
                <div className="lg:col-span-5 glass-card p-6" data-testid="model-radar">
                    <div className="flex items-center gap-2 mb-4">
                        <BarChart3 className="w-4 h-4 text-purple-400" />
                        <h3 className="heading-primary text-sm text-white">MODEL COMPARISON</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={radarData}>
                            <PolarGrid stroke="#333" />
                            <PolarAngleAxis dataKey="source" tick={{ fill: '#888', fontSize: 9 }} />
                            <PolarRadiusAxis tick={{ fill: '#555', fontSize: 8 }} domain={[0, 100]} />
                            <Radar name={d.team_a?.short_name} dataKey="team_a" stroke="#00E5FF" fill="#00E5FF" fillOpacity={0.15} />
                            <Radar name={d.team_b?.short_name} dataKey="team_b" stroke="#FF2D95" fill="#FF2D95" fillOpacity={0.15} />
                            <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>

                {/* Source Detail List */}
                <div className="lg:col-span-7 glass-card p-6 max-h-[440px] overflow-y-auto" data-testid="source-list">
                    <div className="flex items-center gap-2 mb-4">
                        <Users className="w-4 h-4 text-cyan-400" />
                        <h3 className="heading-primary text-sm text-white">ALL PREDICTION MODELS</h3>
                    </div>
                    <div className="space-y-2">
                        {d.expert_sources?.map((s, i) => (
                            <div key={i} className="bg-white/5 rounded-lg overflow-hidden" data-testid={`source-${i}`}>
                                <div className="p-3 flex items-center justify-between cursor-pointer hover:bg-white/10 transition-colors"
                                    onClick={() => setExpandedSource(expandedSource === i ? null : i)}>
                                    <div className="flex items-center gap-3">
                                        {typeIcons[s.type]}
                                        <div>
                                            <div className="text-sm text-white">{s.source}</div>
                                            <div className="text-[10px] text-white/30">{s.methodology}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <div className="text-sm font-bold text-cyan-400">{s.team_a_pct}%</div>
                                        </div>
                                        <div className="w-6">
                                            {expandedSource === i ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
                                        </div>
                                    </div>
                                </div>
                                {expandedSource === i && (
                                    <div className="px-3 pb-3 border-t border-white/5">
                                        <div className="grid grid-cols-3 gap-2 mt-2">
                                            <div className="p-2 bg-white/5 rounded text-center">
                                                <div className="text-[10px] text-white/30">{d.team_a?.short_name}</div>
                                                <div className="text-lg font-bold text-cyan-400">{s.team_a_pct}%</div>
                                            </div>
                                            <div className="p-2 bg-white/5 rounded text-center">
                                                <div className="text-[10px] text-white/30">RELIABILITY</div>
                                                <div className={`text-lg font-bold ${s.reliability > 0.7 ? 'text-green-400' : 'text-yellow-400'}`}>
                                                    {(s.reliability * 100).toFixed(0)}%
                                                </div>
                                            </div>
                                            <div className="p-2 bg-white/5 rounded text-center">
                                                <div className="text-[10px] text-white/30">{d.team_b?.short_name}</div>
                                                <div className="text-lg font-bold text-pink-400">{s.team_b_pct}%</div>
                                            </div>
                                        </div>
                                        <div className="prob-bar h-2 mt-2">
                                            <div className="h-full rounded-full bg-cyan-500 transition-all" style={{ width: `${s.team_a_pct}%` }} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Supporting Data */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="glass-card p-4" data-testid="h2h-record">
                    <div className="text-xs text-white/30 mb-2 tech-mono">HEAD TO HEAD</div>
                    <div className="flex items-center justify-between">
                        <div className="text-center">
                            <div className="text-lg font-bold text-cyan-400">{d.h2h_record?.team_a?.wins || 0}</div>
                            <div className="text-[10px] text-white/30">{d.team_a?.short_name}</div>
                        </div>
                        <div className="text-lg text-white/20">-</div>
                        <div className="text-center">
                            <div className="text-lg font-bold text-pink-400">{d.h2h_record?.team_b?.wins || 0}</div>
                            <div className="text-[10px] text-white/30">{d.team_b?.short_name}</div>
                        </div>
                    </div>
                    <div className="text-[10px] text-white/20 text-center mt-1">{d.h2h_record?.total_matches || 0} total matches</div>
                </div>
                <div className="glass-card p-4" data-testid="form-data">
                    <div className="text-xs text-white/30 mb-2 tech-mono">RECENT FORM (LAST 5)</div>
                    <div className="flex items-center justify-between">
                        <div className="text-center">
                            <div className="text-lg font-bold text-cyan-400">{d.form?.team_a?.wins || 0}W-{d.form?.team_a?.losses || 0}L</div>
                            <div className="text-[10px] text-white/30">{d.team_a?.short_name}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-lg font-bold text-pink-400">{d.form?.team_b?.wins || 0}W-{d.form?.team_b?.losses || 0}L</div>
                            <div className="text-[10px] text-white/30">{d.team_b?.short_name}</div>
                        </div>
                    </div>
                </div>
                <div className="glass-card p-4" data-testid="venue-data">
                    <div className="text-xs text-white/30 mb-2 tech-mono">VENUE HISTORY</div>
                    <div className="flex items-center justify-between">
                        <div className="text-center">
                            <div className="text-lg font-bold text-cyan-400">{(d.venue_history?.team_a?.win_rate * 100).toFixed(0)}%</div>
                            <div className="text-[10px] text-white/30">{d.team_a?.short_name}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-sm text-white/20">{d.venue_history?.venue_total_matches || 0} matches</div>
                        </div>
                        <div className="text-center">
                            <div className="text-lg font-bold text-pink-400">{(d.venue_history?.team_b?.win_rate * 100).toFixed(0)}%</div>
                            <div className="text-[10px] text-white/30">{d.team_b?.short_name}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="text-center tech-mono text-[10px] text-white/20">
                Data source: {d.data_source} | All predictions computed from real Sportmonks historical data
            </div>

            {/* External Intelligence Section */}
            <div className="glass-card p-6 border-l-4 border-l-emerald-500" data-testid="external-intelligence">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-emerald-400" />
                        <h3 className="heading-primary text-sm text-white">EXTERNAL INTELLIGENCE SOURCES</h3>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 tech-mono">
                            {intelStatus?.apify_configured ? 'APIFY' : 'LIVE'}
                        </span>
                        {intelStatus?.apify_configured && (
                            <span className="text-[10px] px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 tech-mono">6 SOURCES</span>
                        )}
                    </div>
                    <Button onClick={handleScrape} disabled={scraping} size="sm"
                        className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                        data-testid="scrape-btn">
                        {scraping ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Scanning...</> : <><Search className="w-3 h-3 mr-1" /> Scan Social Media</>}
                    </Button>
                </div>

                {/* Platform Status */}
                {intelStatus && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {intelStatus.sources?.map(s => (
                            <div key={s.platform} className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] tech-mono ${s.configured ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-white/5 text-white/20 border border-white/5'}`}>
                                {s.configured ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                                {s.platform.replace('_', '/')}
                            </div>
                        ))}
                        {intelStatus.last_scrape && (
                            <span className="text-[10px] text-white/15 tech-mono ml-2">
                                Last scan: {new Date(intelStatus.last_scrape).toLocaleString()}
                            </span>
                        )}
                    </div>
                )}

                {/* Intelligence Results */}
                {intelData && (
                    <div className="space-y-3">
                        {/* Source Summary */}
                        {intelData.sources && (
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                {intelData.sources.map(s => (
                                    <div key={s.source} className="bg-white/5 rounded-lg p-2 text-center">
                                        <div className="text-[10px] text-white/30 tech-mono">{s.source.replace('_', '/')}</div>
                                        <div className={`text-sm font-bold ${s.status === 'success' ? 'text-green-400' : s.status === 'not_configured' ? 'text-white/15' : 'text-amber-400'}`}>
                                            {s.status === 'success' ? `${s.count} insights` : s.status === 'not_configured' ? 'Not set' : s.status}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* GPT Analysis */}
                        {intelData.gpt_analysis?.available && (
                            <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-lg p-4 border border-emerald-500/20">
                                <div className="flex items-center gap-2 mb-2">
                                    <Brain className="w-4 h-4 text-emerald-400" />
                                    <span className="text-xs font-bold text-white">GPT-5.2 INTELLIGENCE ANALYSIS</span>
                                </div>
                                {intelData.gpt_analysis.sentiment && (
                                    <div className="mb-2">
                                        <span className="text-[10px] text-white/30 tech-mono">SENTIMENT: </span>
                                        <span className="text-xs text-white/70">{intelData.gpt_analysis.sentiment}</span>
                                    </div>
                                )}
                                {intelData.gpt_analysis.expert_consensus && (
                                    <div className="mb-2">
                                        <span className="text-[10px] text-white/30 tech-mono">EXPERT CONSENSUS: </span>
                                        <span className="text-xs text-white/70">{intelData.gpt_analysis.expert_consensus}</span>
                                    </div>
                                )}
                                {intelData.gpt_analysis.trends?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {intelData.gpt_analysis.trends.map((t, i) => (
                                            <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{t}</span>
                                        ))}
                                    </div>
                                )}
                                {intelData.gpt_analysis.confidence_adjustment && (
                                    <div className="mt-2">
                                        <span className="text-[10px] text-white/30 tech-mono">AI ADJUSTMENT: </span>
                                        <span className={`text-sm font-bold ${intelData.gpt_analysis.confidence_adjustment > 0 ? 'text-green-400' : intelData.gpt_analysis.confidence_adjustment < 0 ? 'text-red-400' : 'text-white/50'}`}>
                                            {intelData.gpt_analysis.confidence_adjustment > 0 ? '+' : ''}{intelData.gpt_analysis.confidence_adjustment}%
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Raw Insights */}
                        {intelData.all_insights?.some(s => s.insights?.length > 0) && (
                            <div>
                                <div className="text-xs text-white/30 tech-mono mb-2">TOP INSIGHTS</div>
                                <div className="space-y-1 max-h-48 overflow-y-auto">
                                    {intelData.all_insights.flatMap(s =>
                                        (s.insights || []).slice(0, 3).map((ins, i) => ({
                                            ...ins,
                                            source: s.source,
                                            key: `${s.source}-${i}`
                                        }))
                                    ).slice(0, 10).map(ins => (
                                        <div key={ins.key} className="bg-white/3 rounded p-2 flex items-start gap-2">
                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-white/30 tech-mono shrink-0">{ins.source}</span>
                                            <span className="text-xs text-white/50 line-clamp-2">{ins.text || ins.title}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {!intelData.all_insights?.some(s => s.insights?.length > 0) && !intelData.gpt_analysis?.available && (
                            <div className="text-center py-4">
                                <div className="text-xs text-white/20">Configure social media API keys in Settings to enable intelligence gathering</div>
                                <div className="text-[10px] text-white/10 mt-1">YouTube, X/Twitter, Instagram, Facebook, Telegram</div>
                            </div>
                        )}
                    </div>
                )}

                {!intelData && (
                    <div className="text-center py-4">
                        <Search className="w-6 h-6 text-white/10 mx-auto mb-2" />
                        <div className="text-xs text-white/20">Click "Scan Social Media" to fetch external intelligence</div>
                        <div className="text-[10px] text-white/10 mt-1">Scrapes trends, expert opinions, and discussions from configured platforms</div>
                    </div>
                )}
            </div>
        </div>
    );
}
